/**
 * Board state: the single React-side owner of the play document.
 *
 * All document mutation goes through `core` operations; this hook only decides
 * *when* to snapshot for undo and holds the ephemeral editor state (tool,
 * selection, playhead) that never belongs in a saved document.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addEntity,
  amend,
  linkToPlay,
  newPlay,
  openPlay,
  toSavedPlay,
  unlink,
  addKeyframe,
  addShape,
  addStartPhase,
  applyFormation,
  applySavedFormation,
  captureFormation,
  carryOverPoses,
  buildTimeline,
  canAddPlayer as canAddPlayerTo,
  canRedo,
  canRemovePlayer as canRemovePlayerFrom,
  canUndo,
  clearShapes,
  confineShape,
  createDocument,
  createHistory,
  createIdFactory,
  deserialize,
  isPlayerNumberFree as isNumberFree,
  isRoleAtMax,
  moveEntities,
  nextLineupPlayer,
  nextPlayerNumber,
  phaseIndexAt,
  playerToRemove,
  push,
  redo,
  releaseTarget,
  relocateEntities,
  removeEntity,
  removeKeyframe,
  removeShape,
  setEntityLabel,
  setRoleLabel,
  tapStep,
  replace,
  rosterCount,
  sampleAt,
  serialize,
  spawnPosition,
  undo,
  visibleShapes,
  type Entity,
  type EntityMove,
  type IdFactory,
  type PlayDocument,
  type Shape,
  type ShapeKind,
  type SportPack,
  type Timeline,
  type Vec2,
  nextView,
  withView,
  type BoardView,
  type PoseMap,
  type SavedFormation,
} from '../core/index.ts';
import type { BoardStore } from '../adapters/index.ts';
import { Autosaver, type SaveState } from './autosave.ts';

export type Tool = 'select' | 'move' | 'pass' | 'shot' | 'freehand' | 'erase';

export const DRAW_TOOLS: readonly Tool[] = ['move', 'pass', 'shot', 'freehand'];

/** Tools that move the player they are drawn from, rather than only annotating. */
export const CARRY_TOOLS: readonly ShapeKind[] = ['move'];
/** Of those, the ones that take the ball along when the player has it: a swim with the ball is a dribble. */
export const BALL_TOOLS: readonly ShapeKind[] = ['move'];
/** Tools that send the ball away and leave the player standing. */
export const RELEASE_TOOLS: readonly ShapeKind[] = ['pass', 'shot'];

/**
 * What a drawn stroke moves, resolved by the board when the stroke begins.
 *
 * The board previews the stroke through this same description, so committing it
 * verbatim is what stops the commit drifting from what the coach was shown.
 */
export interface Movement {
  readonly moverId: string;
  readonly carriedIds: readonly string[];
  readonly to: Vec2;
  /** The player a pass or a shot is played from, so playback waits for them. */
  readonly fromId?: string;
}

function randomSeed(): string {
  const globalCrypto = globalThis.crypto;
  if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
    return globalCrypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

export interface BoardApi {
  readonly pack: SportPack;
  readonly doc: PlayDocument;
  /** True until the saved board has been fetched; the board is not shown before. */
  readonly loading: boolean;
  readonly saveState: SaveState;
  /** Set when the saved board could not be fetched; the board must not be edited or saved until a reload succeeds. */
  readonly loadError: string | null;
  readonly playhead: number;
  readonly timeline: Timeline;
  readonly durationMs: number;
  readonly isPlaying: boolean;
  readonly activeKeyframeId: string;
  readonly poses: Readonly<Record<string, Vec2>>;
  readonly shapes: readonly Shape[];
  readonly tool: Tool;
  readonly color: string;
  readonly strokeWidth: number;
  readonly selectedIds: readonly string[];
  /** True while more than one piece is selected; only the select tool is usable then. */
  readonly multiSelection: boolean;
  readonly showRoleLabels: boolean;
  readonly showVenueDetail: boolean;
  /** Which stretch of the field is on screen. A camera, not an edit: no undo step. */
  readonly view: BoardView;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly newId: IdFactory;

  setTool(tool: Tool): void;
  setColor(color: string): void;
  setStrokeWidth(width: number): void;
  setSelectedIds(ids: readonly string[]): void;
  setShowRoleLabels(value: boolean): void;
  setShowVenueDetail(value: boolean): void;
  setView(view: BoardView): void;
  /** Full → left half → right half → full. */
  cycleView(): void;

  dragEntities(moves: readonly EntityMove[], commit: boolean): void;
  commitShape(kind: ShapeKind, points: readonly Vec2[], movement?: Movement): void;
  eraseShape(shapeId: string): void;
  clearAnnotations(): void;
  deleteSelected(): void;

  /** Null when the pack declares no roster rule, which hides the controls. */
  readonly rosterMax: number | null;
  rosterCountFor(teamId: string): number;
  /** The role is the one the coach has chosen; both controls act on it. */
  canAddPlayer(teamId: string, roleId?: string): boolean;
  /** Whether the team may hold no more of this role — a cap-independent fact. */
  isRoleAtMax(teamId: string, roleId: string): boolean;
  canRemovePlayer(teamId: string, roleId?: string): boolean;
  isPlayerNumberFree(teamId: string, label: string): boolean;
  addPlayer(teamId: string, options?: { roleId?: string; label?: string }): void;
  /** Gives a player a new number. False, and nothing changes, if it is empty or the team already has it. */
  renamePlayer(entityId: string, label: string): boolean;
  /** Renames a player's position on this board; an empty name restores the role's own. */
  renamePosition(entityId: string, name: string): void;
  removePlayer(teamId: string, roleId?: string): void;

  selectKeyframe(id: string): void;
  addPhase(): void;
  deletePhase(id: string): void;

  seek(t: number): void;
  /**
   * A tap of Space (1) or Shift+Space (-1), or of Next ▸ / ◂ Back: animates
   * one phase on or back, and never waits for a move already under way —
   * see `tapStep`.
   */
  tapPhase(direction: 1 | -1): void;
  /** A key or button held down: plays on smoothly, phase after phase, until released. */
  holdPhase(direction: 1 | -1): void;
  /** Lets go of a hold: the move under way finishes and the board rests on that phase. */
  releasePhase(): void;

  undoAction(): void;
  redoAction(): void;
  /** Opens a saved play on the board: one undo step, and Undo brings back what was there. */
  openSavedPlay(play: PlayDocument): void;
  /** The same teams, the play started over and linked to no saved play. One undo step. */
  startNewPlay(): void;
  /** The board as a saved play under `id` and `name`, ready to store. */
  boardAsPlay(id: string, name: string, createdAt?: number): PlayDocument;
  /** Links the board to the play it was just saved as. Not an edit: no undo step. */
  linkPlay(play: PlayDocument): void;
  /** Forgets the saved play behind the board, e.g. once it is deleted. Not an edit either. */
  unlinkPlay(): void;
  resetTo(formationId: string): void;
  /** Resets the play to one of the coach's saved formations. */
  recallFormation(formation: SavedFormation): void;
  /** Captures the active phase as a formation, ready to store. */
  captureFormation(id: string, name: string, createdAt?: number): SavedFormation;
  /**
   * Set just after a formation is recalled: where each piece stood, and when
   * the glide to its new spot began. The board animates from it; the document
   * already holds the formation.
   */
  readonly glide: { readonly from: PoseMap; readonly startedAt: number } | null;
  loadFromJson(text: string): void;
  toJson(): string;
}

export function useBoard(pack: SportPack, store: BoardStore): BoardApi {
  const idFactoryRef = useRef<IdFactory>(createIdFactory(randomSeed()));
  const newId = idFactoryRef.current;

  const [history, setHistory] = useState(() =>
    createHistory(createDocument({ pack, newId, now: Date.now() })),
  );
  const doc = history.present;

  const [activeKeyframeId, setActiveKeyframeId] = useState(() => doc.keyframes[0]!.id);
  const [playhead, setPlayhead] = useState(0);
  // The playback tick needs to read the playhead the instant after writing it,
  // and a setState updater is only *sometimes* invoked synchronously — React
  // does not guarantee it, so a value read back through one can silently go
  // stale. The ref is always current; `setPlayheadAt` is how every setter of
  // the playhead (in or out of the loop) keeps the two in step.
  const playheadRef = useRef(0);
  const setPlayheadAt = useCallback((value: number) => {
    playheadRef.current = value;
    setPlayhead(value);
  }, []);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tool, setTool] = useState<Tool>('select');
  const [color, setColor] = useState(pack.tools.strokeColor);
  const [strokeWidth, setStrokeWidth] = useState(pack.tools.strokeWidth);
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const multiSelection = selectedIds.length > 1;
  const [showRoleLabels, setShowRoleLabels] = useState(false);
  // Off by default: showing the venue reserves the surround, which shrinks the
  // board. A coach wants the water, not the deck, unless they ask for it.
  const [showVenueDetail, setShowVenueDetail] = useState(false);
  // Kept out of the history on purpose: undoing a move must not also swing
  // the camera. It is written onto the document only on its way to storage.
  const [view, setView] = useState<BoardView>('full');
  const cycleView = useCallback(() => setView((current) => nextView(current)), []);

  const timeline = useMemo(
    () => buildTimeline(doc, { aspectRatio: pack.surface.aspectRatio }),
    [doc, pack.surface.aspectRatio],
  );
  const durationMs = timeline.total;

  /* ---------------- persistence ---------------- */

  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [loadError, setLoadError] = useState<string | null>(null);
  // Owns the debounce/backoff/in-flight bookkeeping so overlapping edits never
  // start a second save while one is still on the wire.
  const autosaverRef = useRef<Autosaver<PlayDocument> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void store
      .load()
      .then((saved) => {
        if (cancelled) return;
        if (saved) {
          setHistory(createHistory(saved));
          setView(saved.view ?? 'full');
        }
        autosaverRef.current = new Autosaver<PlayDocument>(
          { save: (d) => store.save(d), onState: setSaveState },
          saved,
        );
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // A stale editable board here would let the next autosave overwrite
        // the coach's real save, so the board stays locked until a reload works.
        // `cause` need not be an Error — a rejected fetch can reject with
        // anything — so a non-Error rejection still locks the board with a
        // message instead of leaving loadError undefined.
        setLoadError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      autosaverRef.current?.dispose();
    };
  }, [store]);

  // What is stored: the play plus the view it was left in. The same object as
  // `doc` while the view is unchanged, so opening a board does not save it.
  const stored = useMemo(() => withView(doc, view), [doc, view]);

  useEffect(() => {
    if (loading || loadError) return;
    autosaverRef.current?.notify(stored);
  }, [stored, loading, loadError]);

  /* ---------------- playback loop ---------------- */

  // Where the current run of playback stops. Playing to the end and stepping one
  // phase forward are the same thing with a different target.
  const [playTarget, setPlayTarget] = useState(0);

  useEffect(() => {
    if (!isPlaying) return;
    let frame = 0;
    let last = performance.now();
    // Seeded from the phase under the current playhead, not from
    // `activeKeyframeId` itself: a scrub (`seek`) can leave the playhead
    // somewhere `activeKeyframeId` does not agree with, and seeding from that
    // stale value would make the first tick see a false "crossing" back to
    // wherever the playhead actually is.
    let activeId = doc.keyframes[phaseIndexAt(doc, timeline, playheadRef.current)]!.id;
    const tick = (now: number): void => {
      const delta = now - last;
      last = now;
      // Read from the ref, not the state closure: a setState updater is only
      // *sometimes* invoked synchronously, so reading the value back through
      // one right after calling it is not sound. The ref has no such gap.
      const current = playheadRef.current;
      const next = current + delta * Math.sign(playTarget - current || 1);
      const arrived = playTarget >= current ? next >= playTarget : next <= playTarget;
      const landed = arrived ? playTarget : next;
      setPlayheadAt(landed);
      if (arrived) setIsPlaying(false);
      // The active phase is the one on screen, not the one playback started
      // from — update it on a boundary crossing so an edit made mid-play lands
      // on what the coach is actually looking at. This is also what keeps the
      // phase chip honest while a tap or a hold is animating: only a jump
      // (`tapPhase` finishing a move at once) sets it anywhere else.
      const onScreen = doc.keyframes[phaseIndexAt(doc, timeline, landed)];
      if (onScreen && onScreen.id !== activeId) {
        activeId = onScreen.id;
        setActiveKeyframeId(onScreen.id);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // `doc` and `timeline` are read from the closure, not listed here: every
    // path that can change either — `commit` and the undo/redo history
    // rewinds that bypass it — stops playback first, so this loop never runs
    // against a document it can no longer see. The playhead is read through
    // the ref rather than as a dependency, so the loop is never restarted by
    // its own tick.
  }, [isPlaying, playTarget]);

  /* ---------------- derived scene ---------------- */

  const poses = useMemo(() => sampleAt(doc, timeline, playhead), [doc, timeline, playhead]);
  const shapes = useMemo(() => visibleShapes(doc, timeline, playhead), [doc, timeline, playhead]);

  /* ---------------- document mutation ---------------- */

  // Where a recalled formation's pieces glide from; any later edit ends it.
  const [glide, setGlide] = useState<BoardApi['glide']>(null);

  const commit = useCallback((next: PlayDocument) => {
    // The funnel every document mutation goes through, so this is the one place
    // that has to stop playback: a running loop reads `doc`/`timeline` from its
    // closure, and a mutation can change either out from under it.
    setIsPlaying(false);
    setGlide(null);
    setHistory((h) => push(h, next));
  }, []);

  const preview = useCallback((next: PlayDocument) => {
    setHistory((h) => replace(h, next));
  }, []);

  const startOf = useCallback(
    (keyframeId: string) => {
      const index = doc.keyframes.findIndex((k) => k.id === keyframeId);
      return index < 0 ? 0 : (timeline.starts[index] ?? 0);
    },
    [doc, timeline],
  );

  // Timing is derived from the document, so an edit can move where a phase
  // starts. While parked, the board follows the active phase to its new start;
  // left where the phase used to begin, it would sit mid-transition, showing
  // the phase before with a half-way pose. Keyed on the timeline alone: a scrub
  // or a pause changes neither, so neither is overridden.
  useEffect(() => {
    if (isPlaying) return;
    const index = doc.keyframes.findIndex((k) => k.id === activeKeyframeId);
    if (index >= 0) setPlayheadAt(timeline.starts[index] ?? 0);
  }, [timeline]);

  const snapToActiveKeyframe = useCallback(() => {
    const frame = doc.keyframes.find((k) => k.id === activeKeyframeId) ?? doc.keyframes[0]!;
    // An edit stops playback: a running target is only ever valid for the
    // document it was computed against.
    setIsPlaying(false);
    setPlayheadAt(startOf(frame.id));
    return frame.id;
  }, [doc, activeKeyframeId, startOf]);

  const dragEntities = useCallback(
    (moves: readonly EntityMove[], commitDrag: boolean) => {
      const frameId = snapToActiveKeyframe();
      const next = moveEntities(doc, frameId, moves, Date.now());
      if (commitDrag) commit(next);
      else preview(next);
    },
    [doc, snapToActiveKeyframe, commit, preview],
  );

  const commitShape = useCallback(
    (kind: ShapeKind, points: readonly Vec2[], movement?: Movement) => {
      // The board already confines what it hands over; doing it here as well
      // is what makes "no arrow leaves the field" a rule of the document
      // rather than a courtesy of one caller.
      const kept = confineShape(kind, points);
      if (kept.length < 2) return;
      const now = Date.now();
      // Resolved once against the live document so the shape and the movement
      // it describes always land on the same phase, never a stale one.
      const keyframeId = snapToActiveKeyframe();
      const shape: Shape = {
        id: newId('sh'),
        kind,
        points: kept,
        color,
        width: strokeWidth,
        keyframeId,
        // A stroke drawn from a piece is its lane into this phase.
        ...(movement ? { moverId: movement.moverId, carriedIds: movement.carriedIds } : {}),
        ...(movement?.fromId ? { fromId: movement.fromId } : {}),
      };

      let next = addShape(doc, shape, now);

      // The stroke and the movement it describes are one edit, so they go into
      // a single commit and one undo takes back both. The piece lands where the
      // lane ends, in this phase — exactly what the board showed while drawing.
      if (movement) {
        // On the first phase there is nothing to swim from, so a start phase
        // holding everyone where they stood goes in front. The coach stays on
        // the phase they drew on, now the second.
        if (doc.keyframes[0]!.id === keyframeId) next = addStartPhase(next, newId, now);
        next = relocateEntities(next, now, { keyframeId, ...movement });
      }

      commit(next);
    },
    [doc, newId, color, strokeWidth, snapToActiveKeyframe, commit],
  );

  const eraseShape = useCallback(
    (shapeId: string) => commit(removeShape(doc, shapeId, Date.now())),
    [doc, commit],
  );

  const clearAnnotations = useCallback(
    () => commit(clearShapes(doc, activeKeyframeId, Date.now())),
    [doc, activeKeyframeId, commit],
  );

  const deleteSelected = useCallback(() => {
    const ballTypeId = pack.possession?.carriedTypeId;
    // Fold so canRemovePlayer sees the removals already made in this same
    // sweep, rather than judging every id against the untouched document.
    let next = doc;
    let removedAny = false;
    for (const id of selectedIds) {
      const entity = next.entities.find((e) => e.id === id);
      if (!entity || entity.typeId === ballTypeId) continue;
      // canRemovePlayer's floor is zero by design (src/core/roster.ts), so this
      // never blocks a team player on a minimum — it just keeps this sweep from
      // disagreeing with what the roster `−` control itself would do.
      if (pack.roster && entity.teamId !== undefined && !canRemovePlayerFrom(next, pack, entity.teamId, id))
        continue;
      next = removeEntity(next, id, Date.now());
      removedAny = true;
    }
    if (!removedAny) return;
    commit(next);
    setSelectedIds([]);
  }, [doc, pack, selectedIds, commit]);

  /* ---------------- roster ---------------- */

  const addPlayer = useCallback(
    (teamId: string, options: { roleId?: string; label?: string } = {}) => {
      // With nothing specified, `+` adds the next player of the standard
      // lineup — its position, number and spot — so a cleared team refills
      // to the standard seven.
      const standard = options.roleId === undefined && options.label === undefined
        ? nextLineupPlayer(doc, pack, teamId)
        : undefined;
      if (standard) {
        const entity: Entity = {
          id: newId('e'),
          typeId: pack.roster?.playerTypeId ?? 'player',
          teamId,
          roleId: standard.roleId,
          label: standard.label,
        };
        commit(addEntity(doc, entity, standard.at, Date.now()));
        return;
      }
      if (!canAddPlayerTo(doc, pack, teamId, options.roleId)) return;
      const label = options.label?.trim() || nextPlayerNumber(doc, pack, teamId);
      // Two players sharing a number make the board ambiguous, so refuse rather
      // than silently renumber what the coach typed.
      if (!isNumberFree(doc, pack, teamId, label)) return;

      const entity: Entity = {
        id: newId('e'),
        typeId: pack.roster?.playerTypeId ?? 'player',
        teamId,
        ...(options.roleId ? { roleId: options.roleId } : {}),
        label,
      };
      commit(addEntity(doc, entity, spawnPosition(doc, pack, teamId), Date.now()));
    },
    [doc, pack, newId, commit],
  );

  const renamePlayer = useCallback(
    (entityId: string, label: string): boolean => {
      const entity = doc.entities.find((e) => e.id === entityId);
      const text = label.trim();
      if (!entity || !text) return false;
      if (text === entity.label) return true;
      // Two players of one team sharing a number make the board ambiguous.
      if (entity.teamId !== undefined && !isNumberFree(doc, pack, entity.teamId, text)) return false;
      commit(setEntityLabel(doc, entityId, text, Date.now()));
      return true;
    },
    [doc, pack, commit],
  );

  const renamePosition = useCallback(
    (entityId: string, name: string) => {
      const entity = doc.entities.find((e) => e.id === entityId);
      if (!entity || (entity.roleLabel ?? '') === name.trim()) return;
      commit(setRoleLabel(doc, entityId, name, Date.now()));
    },
    [doc, commit],
  );

  const removePlayer = useCallback(
    (teamId: string, roleId?: string) => {
      // A pack with no roster rule gets no controls, and canRemovePlayer says
      // false for it — so removing here would be the one way the two disagree.
      if (!pack.roster) return;
      // A single selected piece states intent; a group does not name any one
      // player, so it falls back to the role/number rule like no selection.
      const focusId = selectedIds.length === 1 ? selectedIds[0]! : null;
      const id = playerToRemove(doc, pack, teamId, focusId, roleId);
      if (!id) return;
      commit(removeEntity(doc, id, Date.now()));
      setSelectedIds((ids) => ids.filter((x) => x !== id));
    },
    [doc, pack, selectedIds, commit],
  );

  /* ---------------- keyframes ---------------- */

  const selectKeyframe = useCallback(
    (id: string) => {
      const frame = doc.keyframes.find((k) => k.id === id);
      if (!frame) return;
      setIsPlaying(false);
      setActiveKeyframeId(id);
      setPlayheadAt(startOf(id));
    },
    [doc, startOf],
  );

  const addPhase = useCallback(() => {
    const next = addKeyframe(doc, newId, Date.now(), { afterKeyframeId: activeKeyframeId });
    commit(next);
    // Select the phase that was just inserted: it sits after the active one.
    const index = next.keyframes.findIndex((k) => k.id === activeKeyframeId);
    const inserted = next.keyframes[index + 1];
    if (inserted) setActiveKeyframeId(inserted.id);
  }, [doc, newId, activeKeyframeId, commit]);

  const deletePhase = useCallback(
    (id: string) => {
      if (doc.keyframes.length <= 1) return;
      const next = removeKeyframe(doc, id, Date.now());
      commit(next);
      // The playhead follows once the new timeline is in (see the effect
      // above); its start here would still be the old one's.
      setActiveKeyframeId(next.keyframes[next.keyframes.length - 1]!.id);
    },
    [doc, commit],
  );

  /* ---------------- transport ---------------- */

  const playTo = useCallback((target: number) => {
    setPlayTarget(target);
    setIsPlaying(true);
  }, []);

  const seek = useCallback(
    (t: number) => {
      setIsPlaying(false);
      setPlayheadAt(Math.max(0, Math.min(durationMs, t)));
    },
    [durationMs],
  );

  const tapPhase = useCallback(
    (direction: 1 | -1) => {
      const step = tapStep(timeline, playheadRef.current, isPlaying ? playTarget : null, direction);
      if (!step) return;
      if (step.snapTo !== undefined) {
        // Finish the move under way at once. The chip is told here: the tick
        // only notices a phase it crosses, and a jump crosses nothing.
        setPlayheadAt(step.snapTo);
        const landed = doc.keyframes[phaseIndexAt(doc, timeline, step.snapTo)];
        if (landed) setActiveKeyframeId(landed.id);
      }
      if (step.target !== undefined) playTo(step.target);
      else setIsPlaying(false);
    },
    [doc, timeline, isPlaying, playTarget, playTo, setPlayheadAt],
  );

  const holdPhase = useCallback(
    (direction: 1 | -1) => {
      const end = direction > 0 ? durationMs : 0;
      if (playheadRef.current !== end) playTo(end);
    },
    [durationMs, playTo],
  );

  const releasePhase = useCallback(() => {
    if (!isPlaying) return;
    const direction = playTarget >= playheadRef.current ? 1 : -1;
    const target = releaseTarget(timeline, playheadRef.current, direction);
    // Only ever pulls a hold's far target in; a tap's move is already one phase long.
    if (Math.abs(target - playheadRef.current) < Math.abs(playTarget - playheadRef.current)) playTo(target);
  }, [timeline, isPlaying, playTarget, playTo]);

  /* ---------------- history & documents ---------------- */

  const undoAction = useCallback(() => {
    // Bypasses `commit` (this rewinds history rather than pushing to it), so it
    // has to stop playback itself: undo can restructure the phases a running
    // loop is reading, e.g. by reverting an `addPhase` or `deletePhase`.
    setIsPlaying(false);
    setHistory((h) => undo(h));
  }, []);
  const redoAction = useCallback(() => {
    setIsPlaying(false);
    setHistory((h) => redo(h));
  }, []);

  // Bookkeeping, not an edit: it must not stop playback or cost an undo step.
  // Applied to whatever the board holds by then, since a save can finish
  // after the coach has gone on drawing.
  const amendDocument = useCallback(
    (change: (present: PlayDocument) => PlayDocument) => setHistory((h) => amend(h, change)),
    [],
  );

  const adoptDocument = useCallback(
    (next: PlayDocument) => {
      // Routed through `commit` rather than `setHistory` directly, so stopping
      // playback lives in one place.
      commit(next);
      const first = next.keyframes[0]!;
      setActiveKeyframeId(first.id);
      // The first phase always starts the timeline at 0.
      setPlayheadAt(0);
      setSelectedIds([]);
    },
    [commit],
  );

  /**
   * Adopts a recalled formation — one undo step, like any reset — and has the
   * board glide each piece there from where it stands on screen now.
   */
  const recallInto = useCallback(
    (next: PlayDocument) => {
      const from = carryOverPoses(doc.entities, poses, next.entities);
      adoptDocument(next);
      setGlide({ from, startedAt: performance.now() });
    },
    [doc, poses, adoptDocument],
  );

  const resetTo = useCallback(
    (formationId: string) => {
      const formation = pack.formations.find((f) => f.id === formationId);
      if (!formation) return;
      recallInto(applyFormation(doc, pack, formation, newId, Date.now()));
    },
    [pack, doc, newId, recallInto],
  );

  const recallFormation = useCallback(
    (formation: SavedFormation) => {
      recallInto(applySavedFormation(doc, formation, pack.roster?.playerTypeId ?? 'player', newId, Date.now()));
    },
    [pack, doc, newId, recallInto],
  );

  const captureCurrentFormation = useCallback(
    (id: string, name: string, createdAt?: number) =>
      captureFormation(doc, activeKeyframeId, {
        id,
        name,
        now: Date.now(),
        ...(createdAt !== undefined ? { createdAt } : {}),
      }),
    [doc, activeKeyframeId],
  );

  const openSavedPlay = useCallback(
    (play: PlayDocument) => {
      adoptDocument(openPlay(play));
      setView(play.view ?? 'full');
    },
    [adoptDocument],
  );

  const startNewPlay = useCallback(() => adoptDocument(newPlay(doc, newId, Date.now())), [doc, newId, adoptDocument]);

  const boardAsPlay = useCallback(
    (id: string, name: string, createdAt?: number) => toSavedPlay(stored, id, name, Date.now(), createdAt),
    [stored],
  );

  const linkPlay = useCallback(
    (play: PlayDocument) => amendDocument((present) => linkToPlay(present, play)),
    [amendDocument],
  );

  const unlinkPlay = useCallback(() => amendDocument(unlink), [amendDocument]);

  const loadFromJson = useCallback(
    (text: string) => {
      // A file is a copy, not the saved play it may have come from.
      const next = unlink(deserialize(text));
      adoptDocument(next);
      setView(next.view ?? 'full');
    },
    [adoptDocument],
  );

  const toJson = useCallback(() => serialize(stored, true), [stored]);

  // Keep the active keyframe pointing at something real after undo/redo/load.
  useEffect(() => {
    if (!doc.keyframes.some((k) => k.id === activeKeyframeId)) {
      const fallback = doc.keyframes[0]!;
      setActiveKeyframeId(fallback.id);
      // The first phase always starts the timeline at 0.
      setPlayheadAt(0);
    }
  }, [doc, activeKeyframeId]);

  return {
    pack,
    doc,
    loading,
    saveState,
    loadError,
    playhead,
    timeline,
    durationMs,
    isPlaying,
    activeKeyframeId,
    poses,
    shapes,
    tool,
    color,
    strokeWidth,
    selectedIds,
    multiSelection,
    showRoleLabels,
    showVenueDetail,
    view,
    canUndo: canUndo(history),
    canRedo: canRedo(history),
    newId,

    setTool,
    setColor,
    setStrokeWidth,
    setSelectedIds,
    setShowRoleLabels,
    setShowVenueDetail,
    setView,
    cycleView,

    dragEntities,
    commitShape,
    eraseShape,
    clearAnnotations,
    deleteSelected,

    rosterMax: pack.roster?.maxPerTeam ?? null,
    rosterCountFor: (teamId: string) => rosterCount(doc, pack, teamId),
    canAddPlayer: (teamId: string, roleId?: string) => canAddPlayerTo(doc, pack, teamId, roleId),
    isRoleAtMax: (teamId: string, roleId: string) => isRoleAtMax(doc, pack, teamId, roleId),
    canRemovePlayer: (teamId: string, roleId?: string) =>
      canRemovePlayerFrom(doc, pack, teamId, selectedIds.length === 1 ? selectedIds[0]! : null, roleId),
    isPlayerNumberFree: (teamId: string, label: string) => isNumberFree(doc, pack, teamId, label),
    addPlayer,
    renamePlayer,
    renamePosition,
    removePlayer,

    selectKeyframe,
    addPhase,
    deletePhase,

    seek,
    tapPhase,
    holdPhase,
    releasePhase,

    undoAction,
    redoAction,
    openSavedPlay,
    startNewPlay,
    boardAsPlay,
    linkPlay,
    unlinkPlay,
    resetTo,
    recallFormation,
    captureFormation: captureCurrentFormation,
    glide,
    loadFromJson,
    toJson,
  };
}
